import { FC, JSX, useContext, useEffect, useMemo, useState } from "react"
import { FlyoutFooter } from "@app/shared/Flyouts";
import { SortDirections, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { Link } from "react-router-dom";
import { sharedEcdDrillInColumnConfig, sharedEcdDrillInCustomSort, sharedEcdDrillInPaginationProps, sharedEcdDrillInSlicedTableData } from "./EcdDrillInConfig";
import { SearchBox } from "@app/shared/SearchBox";

export interface AdequacyBreakdownDrillInTableData {
  [wafflesKeyColumnName]: string;
  projectName: JSX.Element;
  newGaps: string;
  closedGaps: string;
  overallGaps: string;
}

const EcdAdequacyBreakdownFlyout: FC = () => {
  const ecdContext = useContext(EnterpriseComplianceDashboardContext);
  const [data, setData] = useState<AdequacyBreakdownDrillInTableData[]>([]);
  const [filterString, setFilterString] = useState("");
  const [skip, setSkip] = useState(0);
  const take = 100;

  useEffect(() => {
    ecdContext.getAdequacyBreakdownDrillInData().then((response) => {
      const newData: AdequacyBreakdownDrillInTableData[] = response.map((row) => {
        return {
          /* oxlint-disable sort-keys */
          [wafflesKeyColumnName]: row.customProjectId.toString(),
          projectName: <Link key={row.projectName} to={`/projects/${row.customProjectId}/adequacy?viewBy=ALL&all=false&gaps=true`} target="_blank">{row.projectName}</Link>,
          newGaps: row.newGaps.toLocaleString(),
          closedGaps: row.closedGaps.toLocaleString(),
          overallGaps: row.overallGapCount.toLocaleString(),
          /* oxlint-enable sort-keys */
        }
      });

      customSort(newData, "newGaps", SortDirections.Desc);
    })
  }, []);

  const customSort = (records: AdequacyBreakdownDrillInTableData[], column: keyof AdequacyBreakdownDrillInTableData, direction: SortDirections) => {
    const newData = sharedEcdDrillInCustomSort<AdequacyBreakdownDrillInTableData>(
      data.length > 0 ? data : records,
      column,
      direction,
      "newGaps",
      SortDirections.Desc
    );

    setData(newData);
    return records;
  }

  const paginationProps: WafflesPaginationProps = useMemo(() => (
    sharedEcdDrillInPaginationProps<AdequacyBreakdownDrillInTableData>(skip, take, setSkip, data, filterString)
  ), [data, skip, take, filterString]);

  const slicedData = useMemo(() => {
    return sharedEcdDrillInSlicedTableData<AdequacyBreakdownDrillInTableData>(data, filterString, skip, take);
  }, [data, skip, take, filterString]);

  return (
    <>
      <SearchBox
        fieldsetClass="row mx-0"
        inputLabel="Filter on Project Name"
        onSubmit={(value) => setFilterString(value)}
        testId={`ecd-adequacy-breakdown-flyout__filter`}
      />
      <Waffles<AdequacyBreakdownDrillInTableData>
        columns={sharedEcdDrillInColumnConfig}
        customSort={customSort}
        data={slicedData}
        enablePagination={true}
        fullWidth={true}
        loading={data.length === 0 && filterString === ""}
        paginationProps={paginationProps}
        passThroughSorting={true}
        testId="ecd-adequacy-breakdown-flyout__table"
      />
      <FlyoutFooter
        submitButton={null}
      />
    </>
  );
}

export default EcdAdequacyBreakdownFlyout;
